import { useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const queryString = require('query-string');

/**
 * Clears the current selection whenever the route (pathname) changes.
 *
 * The codeable providers can survive navigation (SNavigation stays mounted
 * when the route wrappers are the same component type, e.g. transcript →
 * transcript or survey → survey response), so a pending selection would
 * otherwise carry over to a page it doesn't belong to — its offsets and
 * codeableId still point at the previous page's content.
 *
 * Deep-link selection params (?pos/?snippet/?search) are the exception:
 * pages apply those as a selection on arrival (child effects run before this
 * one), so clearing here would wipe the just-applied selection.
 *
 * Query-param-only changes (pagination, coder filters) never clear — pages
 * that need that handle it locally (e.g. useTranscriptScroll's onPage).
 *
 * @param {Function} onDeselect - Called to clear the selection
 */

// The "coded by me" filter is a pathname suffix on the same page
// (/transcripts/:id/user/me — see UrlUtil.getTranscriptPath), so toggling
// it isn't a page change and must not clear the selection.
const normalizePathname = (pathname) => pathname.replace(/\/user\/me\/?$/, '');

export const useDeselectOnRouteChange = (onDeselect) => {
  const location = useLocation();
  const { snippet, search, pos } = queryString.parse(location.search);
  const hasDeepLinkSelection = !!(snippet || search || pos);

  const pathname = normalizePathname(location.pathname);
  const prevPathnameRef = useRef(pathname);
  useEffect(() => {
    if (prevPathnameRef.current === pathname) return;
    prevPathnameRef.current = pathname;
    if (hasDeepLinkSelection) return;
    onDeselect();
  }, [pathname, hasDeepLinkSelection, onDeselect]);
};
